import React from 'react';
import { Target, CheckCircle, Users, BarChart3, Lightbulb, RefreshCw, TrendingUp } from 'lucide-react';

export default function ProductMarketFit() {
  const framework = [
    {
      phase: 'Phase 1: Problem Validation',
      duration: '2-3 weeks',
      activities: [
        'Customer interview program (50+ conversations minimum)',
        'Pain point severity scoring and frequency analysis',
        'Existing solution evaluation (what are they using now?)',
        'Willingness-to-pay research through price anchoring exercises',
        'Market size estimation and segment identification'
      ]
    },
    {
      phase: 'Phase 2: Solution Hypothesis Testing',
      duration: '3-4 weeks',
      activities: [
        'MVP concept development with minimum viable features',
        'Prototype testing with target customers',
        'Value proposition refinement based on feedback',
        'Feature prioritization using RICE scoring',
        'Positioning and messaging framework development'
      ]
    },
    {
      phase: 'Phase 3: Market Launch & Learning',
      duration: '6-8 weeks',
      activities: [
        'Soft launch to 100 early adopter customers',
        'Usage tracking and behavioral analytics setup',
        'Customer feedback loops (NPS, surveys, interviews)',
        'Cohort analysis and retention measurement',
        'Rapid iteration based on data, not opinions'
      ]
    },
    {
      phase: 'Phase 4: Fit Optimization',
      duration: 'Ongoing',
      activities: [
        'A/B testing on messaging, pricing, and features',
        'Segment-specific customization and positioning',
        'Churn analysis and retention improvement programs',
        'Expansion revenue opportunities (upsells, cross-sells)',
        'Product roadmap aligned with customer demands'
      ]
    }
  ];

  const signals = [
    {
      title: 'The 40-20-10 Rule',
      icon: Target,
      description: "True product-market fit shows: 40%+ of users would be 'very disappointed' without your product, 20%+ organic growth rate month-over-month, 10%+ conversion from visitor to customer. Below these thresholds, you're still searching."
    },
    {
      title: 'Organic Demand Pull',
      icon: TrendingUp,
      description: "When you have PMF, customers find you—not the other way around. Organic traffic grows, referrals multiply, and CAC drops naturally. If you're still pushing hard to acquire customers, PMF remains elusive."
    },
    {
      title: 'Retention Before Acquisition',
      icon: RefreshCw,
      description: "Don't scale acquisition until retention is solid. The leaky bucket problem kills growth. Aim for 60%+ 6-month retention before spending aggressively on new customer acquisition."
    },
    {
      title: 'Customer Language Alignment',
      icon: Users,
      description: "When customers describe your product using your exact value proposition language, you've nailed positioning. When they struggle to explain what you do, messaging needs work."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-16">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center">
              <Target size={32} className="text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold text-[#000000]">
                Product-Market Fit
              </h1>
              <p className="text-xl text-gray-600 mt-2">
                Validate and optimize product-market fit
              </p>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="mb-16">
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <h2 className="text-3xl font-bold mb-4 text-[#000000]">The PMF Problem</h2>
            <p className="text-lg text-gray-700 mb-4">
              Most DTC brands launch products and immediately scale marketing—burning through capital before confirming real product-market fit. They confuse early sales with true PMF, scaling prematurely into the "valley of death."
            </p>
            <p className="text-lg text-gray-700">
              Product-market fit isn't binary—it's a spectrum. Our framework helps you measure where you stand, identify gaps, and systematically improve until you reach the threshold where sustainable, efficient growth becomes possible.
            </p>
          </div>
        </div>

        {/* The Motif Unbound Framework */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">The Systematic PMF Discovery Framework</h2>
          <p className="text-lg text-gray-700 mb-8">
            Product-market fit can't be guessed—it must be measured and improved systematically. Our framework combines qualitative customer research with quantitative metrics to guide you from hypothesis to validated fit.
          </p>
          
          <div className="space-y-6">
            {framework.map((phase, index) => (
              <div key={index} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#000000] mb-2">{phase.phase}</h3>
                    <p className="text-[#38A3A5] font-semibold mb-4">Timeline: {phase.duration}</p>
                  </div>
                </div>
                <ul className="space-y-3 ml-16">
                  {phase.activities.map((activity, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle size={20} className="text-[#38A3A5] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{activity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* PMF Signals */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-4 text-[#000000]">How to Know You've Found PMF</h2>
          <p className="text-lg text-gray-700 mb-8">
            Product-market fit has clear signals. Don't rely on gut feel or vanity metrics. These indicators separate real PMF from wishful thinking:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {signals.map((item, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-[#38A3A5] transition-all">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-xl flex items-center justify-center flex-shrink-0">
                    <item.icon size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#000000]">{item.title}</h3>
                </div>
                <p className="text-gray-700">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">The PMF Scorecard</h2>
          <div className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8">
            <p className="text-lg text-gray-700 mb-6">
              We track these leading indicators to measure PMF strength and guide optimization efforts:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border-2 border-[#38A3A5] rounded-xl p-6">
                <h3 className="font-bold text-xl mb-3 text-[#000000]">Leading Indicators</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#38A3A5] mt-0.5 flex-shrink-0" />
                    <span>Sean Ellis Test Score (% "very disappointed")</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#38A3A5] mt-0.5 flex-shrink-0" />
                    <span>Net Promoter Score (NPS)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#38A3A5] mt-0.5 flex-shrink-0" />
                    <span>Organic traffic growth rate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#38A3A5] mt-0.5 flex-shrink-0" />
                    <span>Referral rate and virality coefficient</span>
                  </li>
                </ul>
              </div>
              <div className="bg-white border-2 border-[#38A3A5] rounded-xl p-6">
                <h3 className="font-bold text-xl mb-3 text-[#000000]">Lagging Indicators</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#38A3A5] mt-0.5 flex-shrink-0" />
                    <span>6-month customer retention rate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#38A3A5] mt-0.5 flex-shrink-0" />
                    <span>Customer lifetime value (LTV)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#38A3A5] mt-0.5 flex-shrink-0" />
                    <span>Customer acquisition cost (CAC) trend</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#38A3A5] mt-0.5 flex-shrink-0" />
                    <span>LTV:CAC ratio (target: 3:1 minimum)</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-[#000000]">Proven Results</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">48%</div>
              <div className="text-lg opacity-90">Average Sean Ellis Score for brands we optimize</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">72%</div>
              <div className="text-lg opacity-90">6-month retention rate achieved</div>
            </div>
            <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-8 text-white">
              <div className="text-5xl font-bold mb-2">4.2:1</div>
              <div className="text-lg opacity-90">Average LTV:CAC ratio at PMF threshold</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#38A3A5] to-[#38A3A5] rounded-2xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">Ready to Find True Product-Market Fit?</h2>
          <p className="text-xl mb-8 opacity-90">
            Let's assess where you stand and build a systematic path to validated PMF.
          </p>
          <a 
            href="mailto:contact@motifunbound.com?subject=Schedule%20a%20Consult&body=Hi%20Motif%20Unbound%20team,%0D%0A%0D%0AI'd%20like%20to%20schedule%20a%20consultation%20to%20discuss%20our%20market%20expansion.%20Please%20share%20available%20times.%0D%0A%0D%0AThank%20you."
            className="inline-block bg-white text-[#38A3A5] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all"
          >
            Schedule a Consult
          </a>
        </div>
      </div>
    </div>
  );
}
